import React, { useState, useMemo, useEffect } from 'react';
import { 
  ArrowLeft, 
  Flame, 
  Target, 
  TrendingUp, 
  Edit3, 
  Trash2,
  CheckCircle,
  Circle,
  Award,
  Activity,

} from 'lucide-react';
import * as Icons from 'lucide-react';
import { Habit, HabitFrequency } from '../../types';
import { useHabitStore } from '../../store/habitStore';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { HabitNotes } from './HabitNotes';
import { validateHabitCompletionForToday, isHabitScheduledForToday } from '../../utils/habitValidation';
import { resolveHabitIcon } from '../../utils/iconMapping';

// Icon mapping - same as in HabitCard
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  activity: Icons.Activity,
  book: Icons.Book,
  coffee: Icons.Coffee,
  dumbbell: Icons.Dumbbell,
  heart: Icons.Heart,
  home: Icons.Home,
  moon: Icons.Moon,
  music: Icons.Music,
  run: Icons.Zap,
  smile: Icons.Smile,
  star: Icons.Star,
  sun: Icons.Sun,
  target: Icons.Target,
  tree: Icons.TreePine,
  droplet: Icons.Droplet,
  flame: Icons.Flame,
  apple: Icons.Apple,
  bike: Icons.Bike,
  camera: Icons.Camera,
  clock: Icons.Clock,
  eye: Icons.Eye,
  gift: Icons.Gift,
  headphones: Icons.Headphones,
  leaf: Icons.Leaf,
  lightbulb: Icons.Lightbulb,
  map: Icons.Map,
  palette: Icons.Palette,
  phone: Icons.Phone,
  shield: Icons.Shield,
  umbrella: Icons.Umbrella,
  circle: Icons.Circle,
  square: Icons.Square,
  triangle: Icons.Triangle,
  diamond: Icons.Diamond,
  hexagon: Icons.Hexagon,
  octagon: Icons.Octagon,
  water: Icons.Droplet,
  zap: Icons.Zap,
};

interface HabitDetailsProps {
  habit: Habit;
  onBack: () => void;
  onEdit?: (habit: Habit) => void;
  onDelete?: (habitId: string) => void;
  onToggleCompletion?: (habitId: string, date?: string) => void;
}

interface CalendarDay {
  date: string;
  isCompleted: boolean;
  isToday: boolean;
  isCurrentMonth: boolean;
}

export const HabitDetails: React.FC<HabitDetailsProps> = ({
  habit,
  onBack,
  onEdit,
  onDelete,
  onToggleCompletion,
}) => {
  const { 
    getCompletionsForHabit, 
    toggleHabitCompletion,
    loadHabitCompletions,
    isLoading,
    error
  } = useHabitStore();
  
  const [selectedView, setSelectedView] = useState<'overview' | 'calendar' | 'analytics' | 'notes'>('overview');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionsLoaded, setCompletionsLoaded] = useState(false);
  
  const completions = getCompletionsForHabit(habit.id);

  // Load completion data when calendar or analytics view is selected
  useEffect(() => {
    if ((selectedView === 'calendar' || selectedView === 'analytics') && !completionsLoaded) {
      loadHabitCompletions(habit.id);
      setCompletionsLoaded(true);
    }
  }, [selectedView, completionsLoaded, loadHabitCompletions, habit.id]);
  // const todaysCompletions = getTodaysCompletions();
  // Prioritize server data over local store - server is the source of truth
  const isCompletedToday = habit.isCompletedToday;
  
  // Use server-calculated streak data from the habit object instead of local calculation
  const streakStats = {
    currentStreak: habit.currentStreak,
    longestStreak: habit.longestStreak,
    completionRate: habit.completionRate,
    totalCompletions: completions.length, // Calculate from local completions data
  };
  
  // Check if habit is scheduled for today
  const isScheduledToday = isHabitScheduledForToday(habit);
  const validationResult = validateHabitCompletionForToday(habit);

  // Get the appropriate icon component
  const IconComponent = ICON_MAP[habit.iconName.toLowerCase()] || resolveHabitIcon(habit.iconName);

  // Calculate days since creation
  const daysSinceCreation = Math.floor(
    (new Date().getTime() - new Date(habit.createdAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  // Generate calendar data for the last 30 days
  const calendarData = useMemo(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 29); // 30 days total including today
    
    const days: CalendarDay[] = [];
    
    // Create a set of completion dates for faster lookup
    const completionDates = new Set(completions.map(c => {
      // Ensure we're comparing dates in the same format
      const completionDate = new Date(c.completedDate);
      return completionDate.toISOString().split('T')[0];
    }));
    
    
    // Add padding days at the beginning to align with Sunday
    const firstDayOfWeek = thirtyDaysAgo.getDay(); // 0 = Sunday, 1 = Monday, etc.
    for (let i = 0; i < firstDayOfWeek; i++) {
      const paddingDate = new Date(thirtyDaysAgo);
      paddingDate.setDate(thirtyDaysAgo.getDate() - (firstDayOfWeek - i));
      const dateStr = paddingDate.toISOString().split('T')[0];
      
      days.push({
        date: dateStr,
        isCompleted: completionDates.has(dateStr),
        isToday: false,
        isCurrentMonth: false, // Padding days are not in current range
      });
    }
    
    // Generate days from 30 days ago to today
    for (let d = new Date(thirtyDaysAgo); d <= today; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const todayStr = today.toISOString().split('T')[0];
      const isToday = dateStr === todayStr;
      const isCompleted = completionDates.has(dateStr);
      
      days.push({
        date: dateStr,
        isCompleted,
        isToday,
        isCurrentMonth: true, // These are the main 30 days we care about
      });
    }
    
    return days;
  }, [completions]);

  // Calculate weekly completion pattern
  const weeklyPattern = useMemo(() => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const pattern = Array(7).fill(0);
    
    completions.forEach(completion => {
      const dayOfWeek = new Date(completion.completedDate).getDay();
      pattern[dayOfWeek]++;
    });
    
    const maxCompletions = Math.max(...pattern);
    
    return dayNames.map((day, index) => ({
      day,
      completions: pattern[index],
      percentage: maxCompletions > 0 ? (pattern[index] / maxCompletions) * 100 : 0,
    }));
  }, [completions]);

  const handleToggleCompletion = async () => {
    // Validate before allowing completion
    if (!validationResult.isValid) {
      alert(validationResult.message);
      return;
    }

    // Prevent multiple simultaneous completion attempts
    if (isCompleting) return;

    setIsCompleting(true);

    try {
      if (onToggleCompletion) {
        // Use the passed handler which will refresh dashboard stats
        await onToggleCompletion(habit.id);
      } else {
        // Fallback to local store only
        await toggleHabitCompletion(habit.id);
      }
    } catch (error) {
      console.error('Failed to toggle habit completion:', error);
      // Could show toast notification here instead of console.error
    } finally {
      setIsCompleting(false);
    }
  };

  const handleEdit = () => {
    onEdit?.(habit);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.(habit.id);
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const getFrequencyDescription = () => {
    switch (habit.frequency) {
      case HabitFrequency.Daily:
        return 'Every day';
      case HabitFrequency.WeeklySpecificDays:
        if (habit.weeklyDays && habit.weeklyDays.length > 0) {
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const selectedDays = habit.weeklyDays.map(day => dayNames[day]).join(', ');
          return `Weekly on ${selectedDays}`;
        }
        return 'Weekly on specific days';
      case HabitFrequency.WeeklyCount:
        return `${habit.weeklyCount || 1} times per week`;
      case HabitFrequency.IntervalBased:
        return `Every ${habit.intervalDays || 1} days`;
      default:
        return 'Daily';
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          leftIcon={<ArrowLeft size={14} />}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm"
        >
          Back
        </Button>
      </div>

      {/* Header */}
      <div className="mb-8">
        {/* Habit Info */}
        <div className="flex items-center space-x-3 mb-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white flex-shrink-0"
            style={{ backgroundColor: habit.colorCode }}
          >
            <IconComponent size={24} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 break-words">
              {habit.name}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {getFrequencyDescription()}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
          {isScheduledToday && (
            <Button
              variant={isCompletedToday ? "success" : "outline"}
              size="sm"
              onClick={handleToggleCompletion}
              disabled={isCompleting}
              isLoading={isCompleting}
              leftIcon={isCompletedToday ? <CheckCircle size={16} /> : <Circle size={16} />}
              className="w-full sm:w-auto"
            >
              {isCompletedToday ? 'Completed' : 'Mark Complete'}
            </Button>
          )}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEdit}
              leftIcon={<Edit3 size={16} />}
              className="flex-1 sm:flex-none"
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              leftIcon={<Trash2 size={16} />}
              className="text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 flex-1 sm:flex-none"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Description */}
      {habit.description && (
        <div className="card p-6 mb-8">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {habit.description}
          </p>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex space-x-1 mb-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button
          onClick={() => setSelectedView('overview')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            selectedView === 'overview'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setSelectedView('calendar')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            selectedView === 'calendar'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          Calendar
        </button>
        <button
          onClick={() => setSelectedView('analytics')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            selectedView === 'analytics'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          Analytics
        </button>
        <button
          onClick={() => setSelectedView('notes')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            selectedView === 'notes'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          Notes
        </button>
      </div>

      {/* Content based on selected view */}
      {selectedView === 'overview' && (
        <div className="space-y-8">
          {/* Key Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="card p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
                  <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Current Streak</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {streakStats.currentStreak}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Longest Streak</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {streakStats.longestStreak}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Completions</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {streakStats.totalCompletions}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {streakStats.completionRate}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress to Target */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Progress to Target
              </h3>
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {Math.min(streakStats.currentStreak, habit.targetStreakLength)}/{habit.targetStreakLength} days
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="h-3 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(
                    (streakStats.currentStreak / habit.targetStreakLength) * 100,
                    100
                  )}%`,
                  backgroundColor: habit.colorCode,
                }}
              />
            </div>
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {streakStats.currentStreak >= habit.targetStreakLength
                ? '🎉 Target achieved! Keep going!'
                : `${habit.targetStreakLength - streakStats.currentStreak} days to reach your target`
              }
            </div>
          </div>

          {/* Habit Info */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Habit Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Created</p>
                <p className="text-gray-900 dark:text-gray-100">
                  {new Date(habit.createdAt).toLocaleDateString()} ({daysSinceCreation} days ago)
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Frequency</p>
                <p className="text-gray-900 dark:text-gray-100">{getFrequencyDescription()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Target Streak</p>
                <p className="text-gray-900 dark:text-gray-100">{habit.targetStreakLength} days</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</p>
                <p className="text-gray-900 dark:text-gray-100">
                  {habit.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedView === 'calendar' && (
        <div className="space-y-6 sm:space-y-8">
          <div className="card p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
              Completion Calendar (Last 30 Days)
            </h3>
            {isLoading && !completionsLoaded && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading completion history...</span>
              </div>
            )}
            {error && (
              <div className="text-red-600 dark:text-red-400 text-sm mb-4">
                {error}
              </div>
            )}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 sm:mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center p-1 sm:p-2">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {calendarData.map((day, index) => (
                <div
                  key={index}
                  className={`
                    aspect-square rounded-sm border text-xs flex items-center justify-center transition-all duration-200
                    ${day.isCompleted
                      ? 'border-green-400 dark:border-green-500 text-white font-semibold'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                    }
                    ${day.isToday
                      ? 'ring-2 ring-blue-500 dark:ring-blue-400'
                      : ''
                    }
                    ${!day.isCurrentMonth
                      ? 'opacity-30'
                      : ''
                    }
                  `}
                  style={{
                    backgroundColor: day.isCompleted ? habit.colorCode : 'transparent',
                  }}
                  title={`${day.date} - ${day.isCompleted ? 'Completed' : 'Not completed'}`}
                >
                  {new Date(day.date).getDate()}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Less</span>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-sm border border-gray-200 dark:border-gray-700"></div>
                <div className="w-3 h-3 rounded-sm border border-gray-200 dark:border-gray-700" style={{ backgroundColor: habit.colorCode + '20' }}></div>
                <div className="w-3 h-3 rounded-sm border border-gray-200 dark:border-gray-700" style={{ backgroundColor: habit.colorCode + '60' }}></div>
                <div className="w-3 h-3 rounded-sm border border-gray-200 dark:border-gray-700" style={{ backgroundColor: habit.colorCode }}></div>
              </div>
              <span>More</span>
            </div>
          </div>
        </div>
      )}

      {selectedView === 'analytics' && (
        <div className="space-y-6 sm:space-y-8">
          {/* Loading State */}
          {isLoading && !completionsLoaded && (
            <div className="card p-4 sm:p-6">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading analytics data...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="card p-4 sm:p-6">
              <div className="text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            </div>
          )}
          {/* Weekly Pattern */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
              Weekly Completion Pattern
            </h3>
            <div className="space-y-2 sm:space-y-3">
              {weeklyPattern.map((day, index) => (
                <div key={index} className="flex items-center space-x-2 sm:space-x-4">
                  <div className="w-8 sm:w-12 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                    {day.day}
                  </div>
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-5 sm:h-6 relative">
                    <div
                      className="h-5 sm:h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-1 sm:pr-2"
                      style={{
                        width: `${day.percentage}%`,
                        backgroundColor: habit.colorCode,
                        minWidth: day.completions > 0 ? '1.5rem' : '0',
                      }}
                    >
                      {day.completions > 0 && (
                        <span className="text-xs font-medium text-white">
                          {day.completions}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
              Recent Activity
            </h3>
            <div className="space-y-3">
              {completions.slice(0, 10).map((completion, index) => (
                <div key={completion.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Completed
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(completion.completedDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {completion.notes && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate">
                      {completion.notes}
                    </p>
                  )}
                </div>
              ))}
              {completions.length === 0 && (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No completions yet</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Complete this habit to see your activity here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'notes' && (
        <HabitNotes habitId={habit.id} habitName={habit.name} />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Habit"
        message={`Are you sure you want to delete "${habit.name}"? This action cannot be undone and will permanently remove all associated data including completion history and notes.`}
        confirmText="Delete Habit"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};